import { FC, ReactElement, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { TFunction, TMessage } from "../i18n/tt";
import { LoggedOnUserInfoDTO } from "../types/UserInfo";
import { Result } from "../common/common";
import { History } from "history";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Alert } from "../components-v2/Alert";
import { getBackUrlFromHistory } from "../meetings/meetingsDomain";
import { NoticeOfBoardMemberView } from "./noticeForBoardmemberComponents";
import {
  MeetingEmailController,
  MeetingEmailInitializerController,
} from "./noticeForBoardMemberDomain";
import {
  useDispatch,
  useRootStore,
  useTranslation,
  useUserStore,
} from "../stores/RootStoreContext";
import LoginPage from "../Login/LoginPage";
import { MeetingLogin } from "../meetings/meetingsComponents";

const NoticeForBoardMemberPage: FC<{ noticeDocumentId: string }> = observer(
  ({ noticeDocumentId }) => {
    const { t } = useTranslation();
    const { http } = useRootStore();
    const dispatch = useDispatch();
    const userStore = useUserStore();
    const history = useRootStore().history;
    const ctrl = useMemo(
      () =>
        new MeetingEmailInitializerController(dispatch, http, (userInfo: LoggedOnUserInfoDTO) => {
          if (!userStore.isAuthenticated) {
            userStore.setUserInfo(userInfo);
          }
        }),
      [dispatch, http, userStore],
    );

    useEffect(() => {
      if (noticeDocumentId) {
        ctrl.fetchDocumentFromMeetingEmail(noticeDocumentId);
      }
    }, [ctrl, noticeDocumentId]);

    function render(
      t: TFunction,
      result: Result<MeetingEmailController, TMessage>,
      history: History,
    ): ReactElement {
      switch (result.state) {
        case "pending":
          return <LoadingIndicator fullscreen />;
        case "failure":
          return <Alert type={"error"} message={t(result.error)} />;
        case "ok":
          if (result.data.isDeleted) {
            return <Alert type={"error"} message={t("noticeForBoardMember.deleted")} />;
          } else {
            const controller = result.data;

            const authStatus = controller.response.authStatus;
            if (authStatus === "LOGIN_NEEDED") {
              return <LoginPage />;
            } else if (authStatus) {
              return (
                <MeetingLogin
                  authStatus={authStatus}
                  companyName={controller.response.companyName}
                  companyLogo={controller.response.companyLogo}
                  meetingPassword={ctrl.meetingPassword}
                  onSubmit={() => {
                    ctrl.fetchDocumentFromMeetingEmail(noticeDocumentId);
                  }}
                />
              );
            }

            return (
              <NoticeOfBoardMemberView
                backUrl={
                  getBackUrlFromHistory(history) ?? (userStore.isAuthenticated ? "/" : undefined)
                }
                data={controller.response}
                onDownloadAttachment={controller.openPreviewAttachmentDialog}
                previewAttachmentState={controller.previewState}
                onCloseAttachmentPreview={controller.closePreviewAttachmentDialog}
                downloadNotice={controller.downloadNotice}
              />
            );
          }
        default:
          return <div />;
      }
    }

    return render(t, ctrl.result, history);
  },
);

export default NoticeForBoardMemberPage;
